import React, { useEffect, useRef, useState } from "react";
import { api, photoUrl, isVideo } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Upload, ArrowUp, ArrowDown } from "lucide-react";
import { Header } from "./TextsTab";

export default function PhotosTab() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [title, setTitle] = useState("");
  const fileRef = useRef();

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/photos"); setPhotos(data); }
    catch (e) { toast.error("No se pudieron cargar las fotos"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) return toast.error("Elegí un archivo");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title);
      await api.post("/admin/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Foto subida");
      setTitle("");
      if (fileRef.current) fileRef.current.value = "";
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al subir"); }
    finally { setUploading(false); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar esta foto?")) return;
    try {
      await api.delete(`/admin/photos/${id}`);
      toast.success("Foto eliminada");
      await load();
    } catch (e) { toast.error("Error al eliminar"); }
  };

  const move = async (idx, dir) => {
    const newArr = [...photos];
    const j = idx + dir;
    if (j < 0 || j >= newArr.length) return;
    [newArr[idx], newArr[j]] = [newArr[j], newArr[idx]];
    setPhotos(newArr);
    try {
      await api.put("/admin/reorder", { collection: "photos", ordered_ids: newArr.map((p) => p.id) });
    } catch (e) { toast.error("Error al reordenar"); }
  };

  const updateTitle = async (p, newTitle) => {
    setPhotos((arr) => arr.map((x) => x.id === p.id ? { ...x, title: newTitle } : x));
  };
  const saveTitle = async (p) => {
    try {
      await api.put(`/admin/photos/${p.id}`, { filename: p.filename, title: p.title, order: p.order });
      toast.success("Título guardado");
    } catch (e) { toast.error("Error"); }
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <Header title="Fotos y videos de la galería" subtitle="Subí imágenes (jpg, png, gif) o videos (mp4, webm). Los videos hacen autoplay en el sitio." />
      <form onSubmit={upload} className="glass rounded-2xl p-6 grid sm:grid-cols-[1fr_auto] gap-4 items-end">
        <div className="grid sm:grid-cols-2 gap-3 flex-1">
          <div>
            <label className="block text-xs text-[#9bb0e0] mb-1.5">Imagen o video</label>
            <input ref={fileRef} type="file" accept="image/*,video/mp4,video/webm,video/quicktime" className="w-full text-sm text-[#cad8ff] file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-[#1a3a8a] file:text-white" />
          </div>
          <div>
            <label className="block text-xs text-[#9bb0e0] mb-1.5">Título</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ej: Recital en vivo" className="w-full bg-[rgba(10,20,55,0.5)] border border-[rgba(120,160,255,0.18)] rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#5fa0ff]" />
          </div>
        </div>
        <button type="submit" disabled={uploading} className="btn-primary px-5 py-2.5 rounded-lg inline-flex items-center gap-2 font-medium disabled:opacity-60">
          <Upload className="w-4 h-4" /> {uploading ? "Subiendo…" : "Subir"}
        </button>
      </form>

      {loading ? <div className="text-[#7da3ff] mono">Cargando…</div> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {photos.map((p, idx) => (
            <div key={p.id} className="glass rounded-xl overflow-hidden">
              <div className="relative aspect-video bg-black/40">
                {isVideo(p)
                  ? <video src={photoUrl(p)} autoPlay muted loop playsInline className="w-full h-full object-cover" />
                  : <img src={photoUrl(p)} alt={p.title} className="w-full h-full object-cover" />}
                <div className="absolute top-2 left-2 mono text-[10px] bg-black/60 px-2 py-1 rounded">#{idx+1}</div>
              </div>
              <div className="p-3 space-y-2">
                <input value={p.title || ""} onChange={(e) => updateTitle(p, e.target.value)} onBlur={() => saveTitle(p)} placeholder="Título…" className="w-full bg-[rgba(10,20,55,0.5)] border border-[rgba(120,160,255,0.18)] rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-[#5fa0ff]" />
                <div className="flex items-center justify-between">
                  <div className="flex gap-1">
                    <button onClick={() => move(idx, -1)} className="w-8 h-8 rounded-lg glass flex items-center justify-center" disabled={idx === 0}><ArrowUp className="w-3 h-3 text-[#9ec0ff]" /></button>
                    <button onClick={() => move(idx, 1)} className="w-8 h-8 rounded-lg glass flex items-center justify-center" disabled={idx === photos.length-1}><ArrowDown className="w-3 h-3 text-[#9ec0ff]" /></button>
                  </div>
                  <button onClick={() => del(p.id)} className="w-8 h-8 rounded-lg glass flex items-center justify-center text-[#ff8a8a]"><Trash2 className="w-3 h-3" /></button>
                </div>
                <div className="mono text-[9px] text-[#5b73a8] truncate">{p.filename}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
